import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DocumentService } from '../../../services/document.service';
import { FileDocument, FileDocumentPage } from '../../../models/document.model';
import { Loading } from '../../layouts/loading/loading';
import { environment } from '../../../../environments/environment';

/** Catégories disponibles */
const CATEGORIES = [
  { key: 'document',       titleKey: 'doc_tab_documents' },
  { key: 'rapport_annuel', titleKey: 'doc_tab_rapports'  },
];

@Component({
  selector: 'app-documents',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, TranslateModule, TranslatePipe, Loading],
  templateUrl: './documents.html',
  styleUrl: './documents.css',
})
export class DocumentsComponent implements OnInit, OnDestroy {
  files: FileDocument[] = [];
  loading  = true;
  error    = '';

  page     = 1;
  perPage  = 12;
  total    = 0;

  /** 'document' | 'rapport_annuel' | '' (tous) */
  activeCategory = 'document';
  searchQuery    = '';

  categories = CATEGORIES;

  private searchSubject = new Subject<string>();
  private paramsSub?: Subscription;
  private searchSub?: Subscription;

  constructor(
    private docService: DocumentService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    // Debounce la saisie de recherche
    this.searchSub = this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
    ).subscribe(() => { this.page = 1; this.updateUrl(); });

    // Réagit aux query params (URL partageable)
    this.paramsSub = this.route.queryParams.subscribe(params => {
      this.activeCategory = params['type']   ?? 'document';
      this.searchQuery    = params['q']      ?? '';
      this.page           = params['page']   ? +params['page'] : 1;
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.paramsSub?.unsubscribe();
    this.searchSub?.unsubscribe();
  }

  /* ── chargement ─────────────────────────────────────────────────── */
  load(): void {
    this.loading = true;
    this.error   = '';
    this.docService
      .getFiles(this.activeCategory, this.searchQuery, this.page, this.perPage)
      .subscribe({
        next: (res: FileDocumentPage) => {
          this.files   = res.data;
          this.total   = res.meta?.total ?? 0;
          this.loading = false;
        },
        error: () => {
          this.error   = 'doc_erreur_chargement';
          this.loading = false;
        },
      });
  }

  /* ── URL ─────────────────────────────────────────────────────────── */
  updateUrl(): void {
    const qp: Record<string, any> = { type: this.activeCategory };
    if (this.searchQuery)  qp['q']    = this.searchQuery;
    if (this.page > 1)     qp['page'] = this.page;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: qp,
      replaceUrl: true,
    });
  }

  /* ── interactions ────────────────────────────────────────────────── */
  selectCategory(key: string): void {
    if (this.activeCategory === key) return;
    this.activeCategory = key;
    this.page = 1;
    this.updateUrl();
  }

  onSearch(value: string): void {
    this.searchQuery = value;
    this.searchSubject.next(value);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.page = 1;
    this.updateUrl();
  }

  loadPage(p: number): void {
    if (p < 1 || p > this.totalPages) return;
    this.page = p;
    this.updateUrl();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /* ── helpers ─────────────────────────────────────────────────────── */
  get totalPages(): number {
    return Math.ceil(this.total / this.perPage);
  }

  get pages(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  get pageTitleKey(): string {
    const cat = this.categories.find(c => c.key === this.activeCategory);
    return cat?.titleKey ?? 'pub_publications';
  }

  /** Construit l'URL de téléchargement absolu */
  downloadUrl(file: FileDocument): string {
    if (file.download_url) {
      // Si déjà absolu
      if (file.download_url.startsWith('http')) return file.download_url;
      return `${environment.apiUrl}${file.download_url}`;
    }
    if (file.file_path) {
      return `${environment.apiUrl}/storage/${file.file_path}`;
    }
    return '#';
  }

  /** Icône CSS selon l'extension */
  fileIcon(type: string): string {
    switch ((type ?? '').toLowerCase()) {
      case 'pdf':  return 'icofont-file-pdf';
      case 'doc':
      case 'docx': return 'icofont-file-word';
      case 'xls':
      case 'xlsx': return 'icofont-file-excel';
      case 'ppt':
      case 'pptx': return 'icofont-file-powerpoint';
      case 'zip':
      case 'rar':  return 'icofont-file-zip';
      default:     return 'icofont-file-text';
    }
  }

  /** Couleur CSS selon l'extension */
  fileColor(type: string): string {
    switch ((type ?? '').toLowerCase()) {
      case 'pdf':          return '#e53e3e';
      case 'doc': case 'docx': return '#3182ce';
      case 'xls': case 'xlsx': return '#38a169';
      case 'ppt': case 'pptx': return '#dd6b20';
      case 'zip': case 'rar':  return '#805ad5';
      default:             return '#718096';
    }
  }

  /** Formate la taille en Ko / Mo */
  formatSize(bytes: number): string {
    if (!bytes) return '—';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  }

  /** Formate la date upload */
  formatDate(d: string): string {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return d; }
  }

  /** Traduction du champ multilingue */
  tf(field: { fr: string; en: string; ar: string } | null | undefined): string {
    if (!field) return '';
    const lang = this.translate.getCurrentLang();
    return (field as any)[lang] || field.fr || field.en || '';
  }
}
